import { useState, VFC, useCallback } from 'react';
import { Button, Col, Row } from 'antd';
import { AccountBookOutlined, CloseOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useAuth } from '@isocel/front-common';
import { useHistory } from 'react-router-dom';

import { AppUser } from '../../queries/api/types';
import PackageCount from './packageCount/PackageCount';
import DashboardFilter from './filter/DashboardFilter';
import PackagingFlowTable from './table/PackagingFlowTable';
import './Dashboard.less';
import { useDashboardContext, DashboardProvider } from '../../context/DashboardContext';
import { downloadCsvFile } from './downloadCsv/downloadCsv';

const Dashboard: VFC = () => {
    const history = useHistory();
    const [showFilters, setShowFilters] = useState(false);
    const {
        selectedStoreIds,
        selectedPackageIds,
        selectedStatus,
        selectedVoucherStatus,
        differenceReported,
        selectedSupplierIds,
        selectedParcPalettIds,
        selectedStartDate,
        selectedEndDate,
        selectedWarehouseIds,
        selectedLitigeStatus,
        selectedBonDeTransport,
        selectedInvoiceName,
        hasTableData,
    } = useDashboardContext();

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };
    const toggleCounter = () => {
        history.push('/counters');
    };
    const { user } = useAuth();
    const appUser = user as AppUser;

    const handleDownloadClick = useCallback(async () => {
        const params = {
            page: 0,
            pageSize: 100,
            differenceReported,
            packageIds: selectedPackageIds,
            status: selectedStatus,
            voucherStatus: selectedVoucherStatus,
            storeIds: selectedStoreIds,
            supplierIds: selectedSupplierIds,
            parcPalettIds: selectedParcPalettIds,
            warehouseIds: selectedWarehouseIds,
            litigeStatus: selectedLitigeStatus,
            bonDeTransport: selectedBonDeTransport,
            invoiceName: selectedInvoiceName || undefined,
            startDate: selectedStartDate ? moment(selectedStartDate) : undefined,
            endDate: selectedEndDate ? moment(selectedEndDate) : undefined,
            organization: user?.organization?.id ?? '',
        };

        await downloadCsvFile(params);
    }, [
        differenceReported,
        selectedPackageIds,
        selectedStatus,
        selectedVoucherStatus,
        selectedStoreIds,
        selectedSupplierIds,
        selectedParcPalettIds,
        selectedWarehouseIds,
        selectedLitigeStatus,
        selectedBonDeTransport,
        selectedInvoiceName,
        selectedStartDate,
        selectedEndDate,
        user?.organization?.id,
    ]);

    // Only redirect if user is loaded and needPasswordChange is true
    if (appUser && appUser.needPasswordChange === true) {
        history.push('/change-password');
        return null;
    }

    return (
        <div>
            <Row justify="space-between" align="middle">
                <Col>
                    <div className="filter-button">
                        <h1>Tableau de bord</h1>
                        <Button
                            type="primary"
                            onClick={toggleFilters}
                            icon={showFilters ? <CloseOutlined /> : <FilterOutlined />}
                        >
                            {showFilters ? 'Fermer les filtres' : 'Filtrer les informations'}
                        </Button>
                    </div>
                </Col>
                <Col>
                    <Button type="primary" onClick={toggleCounter} icon={<AccountBookOutlined />}>
                        {'Counters'}
                    </Button>
                </Col>
            </Row>
            {showFilters && (
                <div className="table-container">
                    <DashboardFilter />
                </div>
            )}
            <PackageCount />
            <Row justify="end">
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadClick}
                    disabled={!hasTableData} // Disable button if no table data
                ></Button>
            </Row>
            <PackagingFlowTable />
        </div>
    );
};

const DashboardWrapper: VFC = () => (
    <DashboardProvider>
        <Dashboard />
    </DashboardProvider>
);

export default DashboardWrapper;
